import { HttpError } from '@bz/request';
import { useSystemAssets } from '@bz/systemAssets';
import { JSX, ErrorBoundary as SolidErrorBoundary } from 'solid-js';
import { captureException } from '@bz/sentry';

type HttpErrorCode = 404 | 408 | 500 | 502 | 503 | 504;

/**
 * 必须使用自定义的ErrorBoundary
 * solid原生的ErrorBoundary出错后在路由变更的时候会重置，
 * 这时候如果children里面有createResource之类的异步资源，那么该资源会被错误的收到成该路由startTransition的依赖
 * 直觉就是一个布局组件出错，会每次子路由变更重新请求，且阻塞真实要渲染的页面
 * 解决方案是在内部包装一个Suspense
 * @param props
 * @returns
 */
export const ErrorBoundary = (
  props: {
    /** ErrorBoundary fallback */
    fallback?: JSX.Element | ((err: any, reset: () => void) => JSX.Element);
  } & ParentProps
) => {
  return <SolidErrorBoundary fallback={props.fallback}>{props.children}</SolidErrorBoundary>;
};

const HTTP_ERROR_CONFIGS: {
  [key in HttpErrorCode]: {
    imgName: keyof ReturnType<typeof useSystemAssets>[0];
    texts?: (() => JSX.Element)[];
  };
} = {
  404: {
    imgName: 'img40440X',
    texts: [() => t("oops! There's a little hiccup."), () => t('The page you requested could not be found.')],
  },
  408: {
    imgName: 'img40840X',
    texts: [() => t("oops! There's a little hiccup."), () => t('The page you requested could not be found.')],
  },
  500: {
    imgName: 'img50050X',
    texts: [() => t('Well, that’s embarrassing…'), () => t('Our server had a little meltdown. We’ll be right back!')],
  },
  502: {
    imgName: 'img50250X',
    texts: [() => t('Bad Gateway.'), () => t('The server received an invalid response. Please try again later.')],
  },
  503: {
    imgName: 'img50350X',
    texts: [() => t('Service Unavailable.'), () => t('The server is temporarily overloaded or down for maintenance.')],
  },
  504: {
    imgName: 'img50450X',
    texts: [() => t('Gateway Timeout.'), () => t('The server took too long to respond. Please try again later.')],
  },
};

export const CustomErrorBoundary = (
  props: ParentProps & {
    fallback?: ((err: any, reset: () => void, fallback: JSX.Element) => JSX.Element) | JSX.Element;
  }
) => {
  const defaultFallback = (err: any, reset: () => void) => {
    const traceId = captureException(err);
    return <DefaultFallback err={err} reset={reset} traceId={traceId} />;
  };
  if (props.fallback) {
    if (typeof props.fallback === 'function') {
      return (
        <ErrorBoundary
          fallback={(err, reset) => {
            const traceId = captureException(err);
            return (props.fallback as (err: any, reset: () => void, fallback: JSX.Element) => JSX.Element)(
              err,
              reset,
              <DefaultFallback err={err} reset={reset} traceId={traceId} />
            );
          }}
        >
          {props.children}
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary
        fallback={(err) => {
          captureException(err);
          return props.fallback as JSX.Element;
        }}
      >
        {props.children}
      </ErrorBoundary>
    );
  }
  return <ErrorBoundary fallback={defaultFallback}>{props.children}</ErrorBoundary>;
};

const DefaultFallback = (props: { err: any; reset: () => void; traceId?: string }) => {
  console.error('DefaultFallback>>>❌❌❌:', props.err);
  return (
    <Show
      when={props.err instanceof HttpError || 'code' in props.err}
      fallback={
        <ErrorFallback
          imgName="devprogressOthers"
          texts={[() => t('Building in Progress'), () => t('We’re crafting something new — check back soon.')]}
          error={props.err}
          reset={props.reset}
          traceId={props.traceId}
        />
      }
    >
      {(_) => {
        const { code } = props.err;
        const config = HTTP_ERROR_CONFIGS[code as keyof typeof HTTP_ERROR_CONFIGS] ?? HTTP_ERROR_CONFIGS[500];

        return (
          <ErrorFallback
            error={props.err}
            imgName={config.imgName}
            texts={config.texts}
            reset={props.reset}
            traceId={props.traceId}
          />
        );
      }}
    </Show>
  );
};
type ErrorFallbackProps = {
  imgName: keyof ReturnType<typeof useSystemAssets>[0];
  texts?: (() => JSX.Element)[];
  error?: any;
  reset?: () => void;
  traceId?: string;
};
const ErrorFallback = (props: ErrorFallbackProps) => {
  const [assets] = useSystemAssets();
  let counter = 0;
  let timer: number | undefined;
  const [detail, setDetail] = createSignal('');
  return (
    <div class="w-full center flex-col p-3">
      <img
        class="max-w-72 w-full"
        src={assets[props.imgName]}
        alt="error"
        onClick={() => {
          timer && clearTimeout(timer);
          counter++;
          setTimeout(() => (counter = 0), 1000);
          if (counter > 4) {
            setDetail(props.error?.stack ?? String(props.error));
          }
        }}
      />
      <div class="flex flex-col gap-4  justify-center items-center mt-6">
        <For each={props.texts}>
          {(text, index) => {
            return (
              <p
                class={cx(
                  index() === 0
                    ? 'uppercase text-2xl text-neutral-main1_alt font-semibold '
                    : 'text-neutral-main5 text-sm font-semibold ',
                  'text-center'
                )}
              >
                {text()}
              </p>
            );
          }}
        </For>
        {/* <Button onClick={props.reset}>{t('Try again')}</Button> */}
      </div>
      <Show when={detail()} keyed>
        <pre class="mt-5">{detail()}</pre>
      </Show>
      <div class="mt-4 flex flex-col items-center gap-2">
        <Show when={props.traceId}>
          {(traceId) => (
            <div
              class="text-xs text-neutral-main5 cursor-pointer hover:text-brand-default transition-colors flex items-center gap-1 active:scale-95 duration-200"
              onClick={() => {
                navigator.clipboard.writeText(traceId());
              }}
              title={t('Click to copy Trace ID')}
            >
              <span>Trace ID: {traceId()}</span>
              <IconCopy class="size-3" />
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};
